import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, FolderOpen, Send, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import uploadService from '../services/uploadService';
import { Capacitor } from '@capacitor/core';
import { CameraService } from '../services/CameraService';

const CameraModal = ({ isOpen, onClose, onPhotoCapture }) => {
  const { i18n } = useTranslation();
  const de = (i18n?.language || '').toLowerCase().startsWith('de');
  const [stream, setStream] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // Start with back camera on mobile
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const initializingRef = useRef(false);
  const loadingRef = useRef(false);
  const retakeFallbackRef = useRef(null);
  const secondRetryRef = useRef(null);

  const isNative = (() => {
    try { return Capacitor?.isNativePlatform?.() === true; } catch { return false; }
  })();

  // Basic mobile detection for render-time decisions
  const isMobileBrowser =
    typeof navigator !== 'undefined' &&
    (/Android/i.test(navigator.userAgent) || /iPad|iPhone|iPod/.test(navigator.userAgent));

  // Enumerate available video input devices (desktop web only)
  const refreshCameraDevices = useCallback(async () => {
    if (isNative) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(device => device.kind === 'videoinput');

      // Deduplicate by deviceId + label to avoid duplicate keys/options
      const deduped = [];
      const seen = new Set();
      videoInputs.forEach((d) => {
        const sigSimple = `${d.deviceId || 'no-id'}-${d.label || 'no-label'}`;
        if (seen.has(sigSimple)) return;
        seen.add(sigSimple);
        deduped.push(d);
      });

      setAvailableCameras(deduped);

      // Ensure current selection is valid; otherwise default to first device
      const hasSelected = selectedCameraId && deduped.some(d => d.deviceId === selectedCameraId);
      if (!hasSelected) {
        setSelectedCameraId(deduped[0]?.deviceId || null);
      }
    } catch (err) {
      console.warn('Camera device enumeration failed:', err);
    }
  }, [isNative, selectedCameraId]);

  const handleNativeCapture = useCallback(async (source = 'camera') => {
    try {
      setIsLoading(true);
      const file = await CameraService.capturePhoto({ source });
      if (file) {
        // Same preview + Send/Add-note choice as the web flow — the native
        // path used to finalize straight into the composer staging area,
        // where beginners never found the send button.
        const imageUrl = URL.createObjectURL(file);
        setCapturedPhoto(prev => {
          if (prev && prev.url) {
            URL.revokeObjectURL(prev.url);
          }
          return { blob: file, url: imageUrl, file };
        });
      }
    } catch (e) {
      console.warn('Native capture failed:', e);
      toast.error('Capture failed');
    } finally {
      setIsLoading(false);
    }
  }, [onPhotoCapture, onClose]);

  // Start camera stream (web only)
  const startCamera = useCallback(async () => {
    if (isNative) return;
    // Prevent multiple simultaneous camera initialization attempts using refs
    if (initializingRef.current || loadingRef.current) {
      return;
    }
    
    initializingRef.current = true;
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);
    setIsVideoReady(false);

    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this device');
      }

      // Stop any existing stream first using functional update
      setStream(currentStream => {
        if (currentStream) {
          currentStream.getTracks().forEach(track => track.stop());
        }
        return null;
      });

      // Wait a bit to ensure camera is released
      await new Promise(resolve => setTimeout(resolve, 100));

      // Mobile-optimized camera constraints
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isMobile = isAndroid || isIOS;
      
      let constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: isIOS ? 1280 : (isAndroid ? 1280 : 1920) },
          height: { ideal: isIOS ? 720 : (isAndroid ? 720 : 1080) }
        },
        audio: false
      };

      // Desktop: prefer explicit device selection when available
      if (!isMobile && selectedCameraId) {
        const { facingMode: _discard, ...rest } = constraints.video;
        constraints.video = {
          ...rest,
          deviceId: { exact: selectedCameraId }
        };
      }

      // iOS-specific constraints
      if (isIOS) {
        constraints.video = {
          ...constraints.video,
          frameRate: { ideal: 30, max: 30 }, // iOS prefers 30fps
          aspectRatio: { ideal: 16/9 }
        };
      }

      let mediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        console.warn('Initial camera constraints failed, trying fallbacks:', err);

        // Desktop: if a specific camera failed, fall back to default device
        if (!isMobile && selectedCameraId && (err.name === 'OverconstrainedError' || err.name === 'NotFoundError')) {
          console.warn('Selected camera failed, clearing selection and retrying default.');
          setSelectedCameraId(null);
          setTimeout(() => startCamera(), 200);
          return;
        }
        
        // Mobile-specific fallbacks
        if (isIOS) {
          try {
            // Try iOS-optimized lower resolution
            constraints = {
              video: {
                facingMode: facingMode,
                width: { ideal: 960 },
                height: { ideal: 540 },
                frameRate: { ideal: 30 }
              },
              audio: false
            };
            mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
          } catch (iOSErr) {
            console.warn('iOS lower-res failed, trying minimal constraints:', iOSErr);
            // Minimal constraints for iOS
            constraints = {
              video: { 
                facingMode: facingMode,
                frameRate: { max: 30 }
              },
              audio: false
            };
            mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
          }
        } else if (isAndroid) {
          try {
            // Try lower resolution for Android
            constraints = {
              video: {
                facingMode: facingMode,
                width: { ideal: 640 },
                height: { ideal: 480 }
              },
              audio: false
            };
            mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
          } catch (androidErr) {
            console.warn('Android lower-res failed, trying minimal constraints:', androidErr);
            // Minimal constraints for Android
            constraints = {
              video: { facingMode: facingMode },
              audio: false
            };
            mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
          }
        } else {
          // Desktop fallback
          constraints = {
            video: { facingMode: facingMode },
            audio: false
          };
          mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        }
      }

      setStream(mediaStream);
      console.log('📹 Camera stream created successfully');

      // Refresh available cameras once permission is granted
      refreshCameraDevices();

      // Attach stream to video element with mobile-specific handling
      if (videoRef.current) {
        const video = videoRef.current;
        
        console.log('📹 Device detection:', { isAndroid, isIOS, isMobile, userAgent: navigator.userAgent });
        
        // Mobile-specific video element preparation
        if (isMobile) {
          video.muted = true;
          video.playsInline = true;
          video.autoplay = true;
          
          // iOS-specific attributes
          if (isIOS) {
            video.setAttribute('webkit-playsinline', 'true');
            video.setAttribute('playsinline', 'true');
            video.controls = false;
            video.preload = 'none'; // iOS prefers explicit loading
          }
          
          // Remove any existing srcObject first for mobile
          video.srcObject = null;
          await new Promise(resolve => setTimeout(resolve, isIOS ? 200 : 150));
        }
        
        video.srcObject = mediaStream;
        
        // Multiple fallback timeouts for different mobile scenarios
        const fallbackTimeout1 = setTimeout(() => {
          if (video.videoWidth > 0 && video.videoHeight > 0) {
            console.log('📹 Video ready via dimensions check');
            setIsVideoReady(true);
            cleanupTimeouts();
          }
        }, isIOS ? 3000 : (isAndroid ? 2500 : 1500));
        
        const fallbackTimeout2 = setTimeout(() => {
          console.log('📹 Video fallback timeout - marking as ready');
          
          // Try one more force play
          if (video.paused) {
            video.play().catch(e => console.log('📹 Force play failed:', e));
          }
          
          setIsVideoReady(true);
          cleanupTimeouts();
        }, isIOS ? 5000 : (isAndroid ? 4000 : 3000));
        
        const cleanupTimeouts = () => {
          clearTimeout(fallbackTimeout1);
          clearTimeout(fallbackTimeout2);
        };
        
        // Set up event handlers before attempting to play
        const handleLoadedMetadata = async () => {
          console.log('📹 Video metadata loaded, starting playback');
          
          if (isIOS) {
            // iOS needs special handling and longer delays
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Force video dimensions update for iOS
            if (video.videoWidth > 0 && video.videoHeight > 0) {
              console.log('📹 iOS: Video has dimensions, proceeding');
              setIsVideoReady(true);
              cleanupTimeouts();
              
              // iOS-specific play sequence
              setTimeout(() => {
                video.load(); // Force reload on iOS
                setTimeout(() => {
                  video.play().catch(err => {
                    console.warn('📹 iOS: Play after metadata failed:', err);
                  });
                }, 100);
              }, 150);
            }
          } else if (isAndroid) {
            // Android needs extra time and specific handling
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // Force video dimensions update
            if (video.videoWidth > 0 && video.videoHeight > 0) {
              console.log('📹 Android: Video has dimensions, proceeding');
              setIsVideoReady(true);
              cleanupTimeouts();
              
              // Try to play after setting ready state
              setTimeout(() => {
                video.play().catch(err => {
                  console.warn('📹 Android: Play after metadata failed:', err);
                });
              }, 100);
            }
          } else {
            setIsVideoReady(true);
            cleanupTimeouts();
            video.play().catch(err => {
              console.error('Video play error:', err);
            });
          }
        };
        
        const handlePlaying = () => {
          console.log('📹 Video is now playing');
          setIsVideoReady(true);
          cleanupTimeouts();
        };
        
        const handleError = (err) => {
          console.error('📹 Video element error:', err);
          setIsVideoReady(false);
          cleanupTimeouts();
        };
        
        const handleCanPlay = () => {
          console.log('📹 Video can play');
          if (isIOS) {
            // iOS: Wait longer and ensure proper initialization
            setTimeout(() => {
              if (video.videoWidth > 0 && video.videoHeight > 0) {
                console.log('📹 iOS: Video ready with dimensions');
                setIsVideoReady(true);
                cleanupTimeouts();
              }
            }, 500);
          } else if (isAndroid) {
            // Android: Wait a bit more before marking ready
            setTimeout(() => {
              if (video.videoWidth > 0 && video.videoHeight > 0) {
                setIsVideoReady(true);
                cleanupTimeouts();
              }
            }, 300);
          } else {
            setIsVideoReady(true);
            cleanupTimeouts();
          }
        };
        
        // Add event listeners
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('playing', handlePlaying);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('error', handleError);
        
        // Mobile-specific stream attachment and play handling
        if (isIOS) {
          console.log('📹 iOS: Using iOS-specific initialization');
          
          // Wait longer for iOS stream attachment
          await new Promise(resolve => setTimeout(resolve, 400));
          
          try {
            // iOS requires specific loading sequence
            video.load(); // Force reload for iOS
            await new Promise(resolve => setTimeout(resolve, 300));
            
            const playPromise = video.play();
            if (playPromise !== undefined) {
              await playPromise;
              console.log('📹 iOS: Video started playing successfully');
              cleanupTimeouts();
              setIsVideoReady(true);
            }
          } catch (iOSPlayErr) {
            console.warn('📹 iOS: Initial play failed, trying recovery:', iOSPlayErr);
            
            // iOS recovery: User interaction might be needed
            setTimeout(async () => {
              try {
                // Force video element refresh for iOS
                const currentStream = video.srcObject;
                video.srcObject = null;
                await new Promise(resolve => setTimeout(resolve, 300));
                video.srcObject = currentStream;
                
                await new Promise(resolve => setTimeout(resolve, 400));
                video.load();
                await new Promise(resolve => setTimeout(resolve, 300));
                
                await video.play();
                console.log('📹 iOS: Recovery successful');
                setIsVideoReady(true);
                cleanupTimeouts();
              } catch (recoveryErr) {
                console.warn('📹 iOS: Recovery failed, using final fallback:', recoveryErr);
                
                // Final iOS fallback
                setTimeout(() => {
                  console.log('📹 iOS: Final fallback - forcing video ready state');
                  setIsVideoReady(true);
                  cleanupTimeouts();
                  
                  // Try one last play attempt
                  video.play().catch(e => console.log('📹 iOS: Final play attempt failed:', e));
                }, 600);
              }
            }, 500);
          }
        } else if (isAndroid) {
          console.log('📹 Android: Using Android-specific initialization');
          
          // Wait for stream to be fully attached
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Try to load and play
          try {
            video.load(); // Force reload for Android
            await new Promise(resolve => setTimeout(resolve, 200));
            
            const playPromise = video.play();
            if (playPromise !== undefined) {
              await playPromise;
              console.log('📹 Android: Video started playing successfully');
              cleanupTimeouts();
              setIsVideoReady(true);
            }
          } catch (androidPlayErr) {
            console.warn('📹 Android: Initial play failed, trying recovery:', androidPlayErr);
            
            // Android recovery: Multiple attempts with delays
            setTimeout(async () => {
              try {
                // Force video element refresh
                const currentStream = video.srcObject;
                video.srcObject = null;
                await new Promise(resolve => setTimeout(resolve, 200));
                video.srcObject = currentStream;
                
                await new Promise(resolve => setTimeout(resolve, 300));
                video.load();
                await new Promise(resolve => setTimeout(resolve, 200));
                
                await video.play();
                console.log('📹 Android: Recovery successful');
                setIsVideoReady(true);
                cleanupTimeouts();
              } catch (recoveryErr) {
                console.warn('📹 Android: Recovery failed, using final fallback:', recoveryErr);
                
                // Final Android fallback
                setTimeout(() => {
                  console.log('📹 Android: Final fallback - forcing video ready state');
                  setIsVideoReady(true);
                  cleanupTimeouts();
                  
                  // Try one last play attempt
                  video.play().catch(e => console.log('📹 Android: Final play attempt failed:', e));
                }, 500);
              }
            }, 400);
          }
        } else {
          // Non-Android devices (original logic)
          try {
            await video.play();
            console.log('📹 Video started playing');
            cleanupTimeouts();
            setIsVideoReady(true);
          } catch (playErr) {
            console.warn('Initial play failed, trying Firefox workarounds:', playErr);
            
            // Firefox workaround: Force video refresh
            setTimeout(async () => {
              try {
                // Re-assign the stream to force refresh
                video.srcObject = null;
                await new Promise(resolve => setTimeout(resolve, 100));
                video.srcObject = mediaStream;
                
                console.log('📹 Firefox: Re-attached stream, attempting play');
                await video.play();
                console.log('📹 Firefox: Video playing after refresh');
                setIsVideoReady(true);
                cleanupTimeouts();
              } catch (retryErr) {
                console.warn('Firefox retry failed:', retryErr);
                // Final fallback - just show the video anyway
                setTimeout(() => {
                  console.log('📹 Firefox final fallback: Showing video anyway');
                  setIsVideoReady(true);
                  cleanupTimeouts();
                }, 200);
              }
            }, 300);
          }
        }
      }
    } catch (err) {
      console.error('Camera access error:', err);
      let errorMessage = 'Failed to access camera';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera permission denied. Please allow camera access and try again.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotSupportedError') {
        errorMessage = 'Camera not supported on this device.';
      } else if (err.name === 'NotReadableError' || err.message.includes('videosource')) {
        errorMessage = 'Camera is busy or in use by another application. Please close other apps using the camera and try again.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera settings not supported. Trying basic camera...';
        // Retry with minimal constraints
        setTimeout(() => {
          setFacingMode('user');
          startCamera();
        }, 1000);
        return;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      loadingRef.current = false;
      initializingRef.current = false;
      setIsLoading(false);
    }
  }, [facingMode, isNative, selectedCameraId, refreshCameraDevices]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (isNative) return;
    initializingRef.current = false; // Reset initialization flag
    loadingRef.current = false; // Reset loading flag
    setIsVideoReady(false); // Reset video ready state
    
    setStream(currentStream => {
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
      }
      return null;
    });
    
    if (videoRef.current) {
      const video = videoRef.current;
      video.srcObject = null;
      
      // Remove event listeners if they exist
      video.removeEventListener('loadedmetadata', () => {});
      video.removeEventListener('playing', () => {});
      video.removeEventListener('canplay', () => {});
      video.removeEventListener('error', () => {});
    }
  }, [isNative]);

  // Take photo with Android validation
  const takePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) {
      toast.error('Camera not ready');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMobile = isAndroid || isIOS;

    // Additional validation for mobile devices
    if (isMobile) {
      console.log(`📹 ${isIOS ? 'iOS' : 'Android'} photo capture validation:`, {
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        readyState: video.readyState,
        paused: video.paused
      });

      // Ensure video has valid dimensions
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.warn(`📹 ${isIOS ? 'iOS' : 'Android'}: Video has no dimensions, cannot capture`);
        toast.error('Video not ready. Please wait a moment and try again.');
        return;
      }

      // Check if video is actually playing content
      if (video.readyState < 2) {
        console.warn(`📹 ${isIOS ? 'iOS' : 'Android'}: Video not in ready state`);
        toast.error('Video is loading. Please wait a moment and try again.');
        return;
      }
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    console.log('📹 Taking photo:', {
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height
    });

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Additional check for black image on mobile devices
    if (isMobile) {
      // Sample a few pixels to check if the image is completely black
      const imageData = context.getImageData(canvas.width / 2, canvas.height / 2, 1, 1);
      const pixel = imageData.data;
      const pixelSum = pixel[0] + pixel[1] + pixel[2]; // RGB sum
      
      if (pixelSum === 0) {
        console.warn(`📹 ${isIOS ? 'iOS' : 'Android'}: Detected black image, retrying capture`);
        
        // Try to refresh the video stream and capture again
        setTimeout(() => {
          if (video.videoWidth > 0 && video.videoHeight > 0) {
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            canvas.toBlob((blob) => {
              if (blob) {
                const imageUrl = URL.createObjectURL(blob);
                setCapturedPhoto({ blob, url: imageUrl });
                console.log(`📹 ${isIOS ? 'iOS' : 'Android'}: Retry capture successful`);
              } else {
                toast.error('Failed to capture photo after retry');
              }
            }, 'image/jpeg', 0.9);
          } else {
            toast.error('Video stream lost. Please try again.');
          }
        }, isIOS ? 150 : 100); // iOS gets slightly longer delay
        return;
      }
    }

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        const imageUrl = URL.createObjectURL(blob);
        setCapturedPhoto({ blob, url: imageUrl });
        console.log('📹 Photo captured successfully');
      } else {
        toast.error('Failed to capture photo');
      }
    }, 'image/jpeg', 0.9);
  }, []);

  // Finalize captured photo with explicit action
  const finalizePhoto = useCallback((autoSend) => {
    if (!capturedPhoto || !capturedPhoto.blob) return;
    // Uploaded files keep their original name/type; camera blobs get wrapped.
    const file = capturedPhoto.file instanceof File
      ? capturedPhoto.file
      : new File([capturedPhoto.blob], `camera-photo-${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
    setTimeout(() => {
      onPhotoCapture(file, { autoSend });
      onClose();
    }, 0);
  }, [capturedPhoto, onPhotoCapture, onClose]);

  // Handle file selection
  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Manual restart control
  const handleManualRestart = useCallback(() => {
    console.log('📹 Manual restart requested');
    setIsVideoReady(false);
    setIsLoading(true);
    stopCamera();
    setTimeout(() => {
      startCamera();
    }, 500);
  }, [stopCamera, startCamera]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = uploadService.validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = ''; // Clear input
      return;
    }

    // Route the upload through the same preview + Send/Add-note choice as a
    // camera capture. Bypassing it (the old behavior) silently staged the
    // photo in the composer — testers who uploaded instead of snapping never
    // saw a Send button and stranded their photos (found Aug 2).
    const imageUrl = URL.createObjectURL(file);
    setCapturedPhoto(prev => {
      if (prev && prev.url) {
        URL.revokeObjectURL(prev.url);
      }
      return { blob: file, url: imageUrl, file };
    });
    e.target.value = '';
  }, []);

  // Retake photo - always do a clean camera restart for reliability
  const retakePhoto = useCallback(() => {
    console.log('📹 Retake: Starting clean restart');
    
    // Clear any pending fallback timers
    if (retakeFallbackRef.current) {
      clearTimeout(retakeFallbackRef.current);
      retakeFallbackRef.current = null;
    }
    if (secondRetryRef.current) {
      clearTimeout(secondRetryRef.current);
      secondRetryRef.current = null;
    }
    
    // Revoke the captured photo URL
    setCapturedPhoto(prev => {
      if (prev && prev.url) {
        URL.revokeObjectURL(prev.url);
      }
      return null;
    });

    // Native: no in-app stream to restart — clearing the photo brings the
    // native capture buttons back. (Restart logic would strand isLoading=true
    // because startCamera no-ops on native.)
    if (isNative) {
      setIsLoading(false);
      return;
    }

    // Reset state and do a full camera restart
    setIsVideoReady(false);
    setIsLoading(true);
    
    // Stop and restart camera - this is the most reliable approach
    stopCamera();
    setTimeout(() => {
      startCamera();
    }, 300);
  }, [startCamera, stopCamera, isNative]);

  // Handle modal close
  const handleClose = useCallback(() => {
    setCapturedPhoto(prev => {
      if (prev && prev.url) {
        URL.revokeObjectURL(prev.url);
      }
      return null;
    });
    setError(null);
    setIsVideoReady(false);
    onClose();
  }, [onClose]);

  // Start camera when modal opens
  useEffect(() => {
    if (isOpen) {
      if (!isNative) {
        refreshCameraDevices();
        setTimeout(() => { startCamera(); }, 100);
      }
    } else {
      stopCamera();
      setCapturedPhoto(null);
      setError(null);
      setIsVideoReady(false);
    }
  }, [isOpen, startCamera, stopCamera, isNative, refreshCameraDevices]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (capturedPhoto && capturedPhoto.url) {
        URL.revokeObjectURL(capturedPhoto.url);
      }
      if (retakeFallbackRef.current) {
        clearTimeout(retakeFallbackRef.current);
        retakeFallbackRef.current = null;
      }
      if (secondRetryRef.current) {
        clearTimeout(secondRetryRef.current);
        secondRetryRef.current = null;
      }
    };
  }, [stream, capturedPhoto]);

  if (!isOpen) return null;

  // z-[60000] keeps the modal above the mobile bottom nav (z-[50000] in
  // Layout.jsx), which otherwise overlaps the shutter / Send controls on
  // phones. Paywall / disclosure dialogs (70000+) still stack on top.
  return createPortal(
    <div className="fixed inset-0 bg-black z-[60000] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-black/50 text-white">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="text-lg font-semibold">Camera</h2>
          {/* Desktop camera selector - only shown when multiple cameras available and not on mobile */}
          {!isNative && !isMobileBrowser && availableCameras.length > 1 && (
            <div className="flex items-center gap-2 min-w-0">
              <select
                value={selectedCameraId || ''}
                onChange={(e) => {
                  const newId = e.target.value || null;
                  setSelectedCameraId(newId);
                  setIsVideoReady(false);
                  stopCamera();
                  setTimeout(() => {
                    startCamera();
                  }, 200);
                }}
                className="bg-white/10 border border-white/20 text-white rounded-md px-2 py-1 text-sm max-w-[200px] truncate focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Select camera"
              >
                {availableCameras.map((cam, idx) => (
                  <option
                    key={`${cam.deviceId || 'cam'}-${idx}`}
                    value={cam.deviceId}
                    className="bg-black text-white"
                  >
                    {cam.label || `Camera ${idx + 1}`}
                  </option>
                ))}
              </select>
              <button
                onClick={refreshCameraDevices}
                className="h-6 px-1 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full transition-colors flex items-center justify-center"
                title="Refresh cameras"
                aria-label="Refresh cameras"
              >
                <RefreshCw size={14} />
              </button>
            </div>
          )}
        </div>
        <button
          onClick={handleClose}
          className="p-2 hover:bg-white/20 rounded-full transition-colors"
          title="Close camera"
        >
          <X size={24} />
        </button>
      </div>

      {/* Camera view — the captured/uploaded photo preview wins on EVERY
          platform: it is the review moment that carries the Send / Add-note
          choice below. */}
      <div className="flex-1 relative overflow-hidden">
        {capturedPhoto ? (
          <div className="h-full flex items-center justify-center bg-black">
            <img
              src={capturedPhoto.url}
              alt="Captured preview"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        ) : isNative ? (
          <div className="flex flex-col items-center justify-center h-full text-white p-8 text-center gap-4">
            <button
              onClick={() => handleNativeCapture('camera')}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
              disabled={isLoading}
            >
              <Camera size={18} /> Take Photo
            </button>
            <button
              onClick={() => handleNativeCapture('gallery')}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors border border-white/20"
              disabled={isLoading}
            >
              <FolderOpen size={18} /> Choose from Library
            </button>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-white p-8 text-center">
            <div className="text-red-400 mb-4">⚠️</div>
            <p className="text-lg mb-4">{error}</p>
            {error.includes('busy') || error.includes('videosource') ? (
              <div className="mb-4 text-sm text-gray-300">
                <p>Tips to fix camera issues:</p>
                <ul className="list-disc text-left mt-2 space-y-1">
                  <li>Close other apps using the camera</li>
                  <li>Refresh the browser page</li>
                  <li>Try switching camera modes</li>
                  <li>Restart your browser</li>
                </ul>
              </div>
            ) : null}
            <div className="flex gap-3 justify-center">
              <button
                onClick={startCamera}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Try Again
              </button>
              {facingMode === 'environment' && (
                <button
                  onClick={() => {
                    setFacingMode('user');
                    setTimeout(startCamera, 200);
                  }}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  Try Front Camera
                </button>
              )}
            </div>
          </div>
        ) : !isNative ? (
          // Show camera feed
          <div className="h-full flex items-center justify-center bg-black relative">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-center bg-black z-10">
                <div>
                  <div className="text-2xl mb-2">📷</div>
                  <p>Starting camera...</p>
                  <button
                    onClick={handleManualRestart}
                    className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                  >
                    Restart Camera
                  </button>
                </div>
              </div>
            )}
            {!isVideoReady && !isLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-center bg-black z-10">
                <div>
                  <div className="text-2xl mb-2">📹</div>
                  <p>Loading video feed...</p>
                  <p className="text-sm mt-2 text-gray-400">This may take a moment...</p>
                  {stream && (
                    <button
                      onClick={handleManualRestart}
                      className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                    >
                      Restart Camera
                    </button>
                  )}
                </div>
              </div>
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              controls={false}
              className="w-full h-full object-cover"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                backgroundColor: 'black',
                display: 'block'
              }}
              onLoadedData={() => {
                console.log('📹 Video loadeddata event fired');
                const video = videoRef.current;
                const isAndroid = /Android/i.test(navigator.userAgent);
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                
                if (isIOS && video && video.videoWidth > 0 && video.videoHeight > 0) {
                  console.log('📹 iOS: loadeddata with valid dimensions');
                  setTimeout(() => setIsVideoReady(true), 200);
                } else if (isAndroid && video && video.videoWidth > 0 && video.videoHeight > 0) {
                  console.log('📹 Android: loadeddata with valid dimensions');
                  setIsVideoReady(true);
                } else if (!isAndroid && !isIOS) {
                  setIsVideoReady(true);
                }
              }}
              onCanPlayThrough={() => {
                console.log('📹 Video canplaythrough event fired');
                const video = videoRef.current;
                const isAndroid = /Android/i.test(navigator.userAgent);
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                
                if (isIOS && video && video.videoWidth > 0 && video.videoHeight > 0) {
                  console.log('📹 iOS: canplaythrough with valid dimensions');
                  setTimeout(() => setIsVideoReady(true), 300);
                } else if (isAndroid && video && video.videoWidth > 0 && video.videoHeight > 0) {
                  console.log('📹 Android: canplaythrough with valid dimensions');
                  setTimeout(() => setIsVideoReady(true), 100);
                } else if (!isAndroid && !isIOS) {
                  setIsVideoReady(true);
                }
              }}
            />
          </div>
        ) : null}

        {/* Hidden canvas for photo capture */}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Controls */}
      <div
        className="p-6 m-3 bg-black/50"
        style={{ paddingBottom: 'calc(var(--app-safe-area-bottom, 0px) + 1.5rem)' }}
      >
        {capturedPhoto ? (
          // Photo captured controls
          <div className="w-full max-w-2xl mx-auto grid grid-cols-3 gap-3 items-center">
            <button
              onClick={retakePhoto}
              className="w-full px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors border border-white/20"
              title={de ? 'Neues Foto machen' : 'Retake photo'}
            >
              {de ? 'Nochmal' : 'Retake'}
            </button>
            <button
              onClick={() => finalizePhoto(true)}
              className="w-full px-2 py-7 bg-green-600 hover:bg-green-700 text-white rounded-full transition-colors flex items-center justify-center gap-2 shadow"
              title={de ? 'Jetzt senden' : 'Send now'}
            >
              <Send size={18} />
              {de ? 'Senden' : 'Send'}
            </button>
            {/* Secondary path: attach to the composer to write a note first.
                Deliberately NOT a checkmark — a ✓ reads as "confirm/done" and
                strands beginners with a staged-but-unsent photo. The label
                must say what it costs: writing comes next, not done. */}
            <button
              onClick={() => finalizePhoto(false)}
              className="w-full px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors flex items-center justify-center gap-2 border border-white/20"
              title={de ? 'Anhängen und zuerst eine Notiz schreiben' : 'Attach and write a note first'}
            >
              {/* <MessageSquarePlus size={18} /> */}
              {de ? 'Mit Notiz' : 'Add note'}
            </button>
          </div>
        ) : (
          // Camera controls
          <div className="relative flex items-center justify-center gap-10">
            {/* File upload */}
            <button
              onClick={handleFileSelect}
              className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Choose from files"
              aria-label="Choose from files"
            >
              <FolderOpen size={18} />
            </button>
            {/* Shutter */}
            <button
              onClick={takePhoto}
              disabled={isLoading || error || !isVideoReady}
              className="w-20 h-20 bg-white hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow"
              title={isVideoReady ? "Take photo" : "Waiting for camera..."}
              aria-label="Shutter"
            >
              <Camera size={26} className="text-black" />
            </button>
            {/* Flip camera */}
            {navigator.mediaDevices && navigator.mediaDevices.enumerateDevices && (
              <button
                onClick={async () => {
                  const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
                  setFacingMode(newFacingMode);
                  stopCamera();
                  setTimeout(() => {
                    startCamera();
                  }, 300);
                }}
                disabled={isLoading || error || !isVideoReady}
                className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Flip camera"
                aria-label="Flip camera"
              >
                <RefreshCw size={20} />
              </button>
            )}
           
          </div>
        )}
      </div>
      
      {/* Hidden File Input */}
      {!isNative && (
        <input
          ref={fileInputRef}
          type="file"
          accept={uploadService.getSupportedMimeTypes()}
          onChange={handleFileChange}
          className="hidden"
        />
      )}
    </div>,
    document.body
  );
};

export default CameraModal;
